import { Formik, Form as FormikForm } from 'formik';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import Col from '../../components/Col';
import Form from '../../components/Form';
import TextInput from '../../components/TextInput';
import DataPolicyModal from '../Modals/DataPolicy';
import TermsModal from '../Modals/Terms';
import Button from '../../components/Button';
import Text from '../../components/Text';
import ErrorBox from '../../components/ErrorBox';
import CenteredPage from '../../containers/CenteredPage';
import Box from '../../components/layout/Box';

const SignUpView = ({
  handleSignUp,
  loading,
  error,
  checkError,
  setCheckError,
  setChecked,
  checked,
  showPrivacyPolicyModal,
  setShowPrivacyPolicyModal,
  showTermsModal,
  setShowTermsModal,
  initialValues,
  validationSchema,
  openModal,
}) => {
  const getErrorMessage = () => {
    if (error) {
      switch (error) {
        case 'UsernameExistsException':
          return (
            <FormattedMessage defaultMessage="En användare med den här e-postadressen finns redan registrerad." />
          );
        case 'UserNotFoundException':
          return (
            <FormattedMessage defaultMessage="Konto med angiven e-postadress och lösenord kunde ej hittas." />
          );
        case 'InvalidParameterException':
          return (
            <FormattedMessage defaultMessage="Se till att mobilnumret är giltigt och börjar med +46 eller annan landskod" />
          );
        default:
        case 'UnknownError':
          return (
            <FormattedMessage defaultMessage="Ett fel har inträffat. Var god försök igen." />
          );
      }
    }
  };

  return (
    <>
      <CenteredPage className="py-5 flex-column" style={{ maxWidth: 520 }}>
        <h1 className="mb-3">
          <FormattedMessage defaultMessage="Skapa konto" />
        </h1>

        {error && (
          <ErrorBox
            title={<FormattedMessage defaultMessage="Fel vid registrering" />}
            text={getErrorMessage()}
          />
        )}

        <Box className="flex-column w-100">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values) => {
              handleSignUp(values);
            }}
          >
            {({ values, errors, handleChange, handleBlur, submitCount }) => {
              const showErrors = submitCount > 0;

              return (
                <FormikForm>
                  <TextInput
                    type="text"
                    name="name"
                    onChange={handleChange('name')}
                    onBlur={handleBlur}
                    value={values.name}
                    error={showErrors && errors.name}
                    placeholder={<FormattedMessage defaultMessage="Namn" />}
                  />

                  <TextInput
                    type="text"
                    name="email"
                    onChange={handleChange('email')}
                    onBlur={handleBlur}
                    value={values.email}
                    error={showErrors && errors.email}
                    placeholder={
                      <FormattedMessage defaultMessage="E-postadress" />
                    }
                  />

                  <TextInput
                    type="text"
                    name="phone"
                    onChange={handleChange('phone')}
                    onBlur={handleBlur}
                    value={values.phone}
                    error={showErrors && errors.phone}
                    placeholder={
                      <FormattedMessage defaultMessage="Mobilnummer (valfritt)" />
                    }
                  />

                  <TextInput
                    type="password"
                    name="password"
                    onChange={handleChange('password')}
                    onBlur={handleBlur}
                    value={values.password}
                    error={showErrors && errors.password}
                    placeholder={<FormattedMessage defaultMessage="Lösenord" />}
                  />

                  <div className="d-flex w-100 flex-column">
                    <div className="d-flex flex-row">
                      <div
                        className="d-flex flex-row w-100"
                        style={{
                          marginRight: 8,
                        }}
                      >
                        <div>
                          <FormattedMessage defaultMessage="Jag har läst och godkänner " />{' '}
                          <Link onClick={() => openModal('userAgreement')}>
                            <FormattedMessage defaultMessage="användarvillkoren" />
                          </Link>{' '}
                          <FormattedMessage defaultMessage="och" />{' '}
                          <Link onClick={() => openModal('privacyPolicy')}>
                            <FormattedMessage defaultMessage="datapolicyn" />
                          </Link>
                        </div>
                      </div>
                      <Form.Check
                        isInvalid={checkError}
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          setChecked((prev) => {
                            if (!prev) {
                              setCheckError(false);
                            }

                            return !prev;
                          });
                        }}
                        id={`default-checkbox`}
                      />
                    </div>
                    {checkError && (
                      <Text
                        style={{ color: 'red', fontSize: 14 }}
                        className="mt-1"
                      >
                        <FormattedMessage defaultMessage="Du måste godkänna användarvillkoren och vår datapolicy" />
                      </Text>
                    )}
                  </div>

                  <div className="d-flex flex-row w-100 mt-3 mb-2">
                    <Button expand type="submit" isLoading={loading}>
                      <FormattedMessage defaultMessage="Skapa konto" />
                    </Button>
                  </div>
                </FormikForm>
              );
            }}
          </Formik>

          <Box className="mt-2">
            <Col style={{ textAlign: 'center' }}>
              <FormattedMessage
                defaultMessage="Har du redan ett konto?"
                description="Text prior to link to login page"
              />{' '}
              <Link to="/sign-in">
                <FormattedMessage
                  defaultMessage="Logga in"
                  description="Link to login page"
                />
              </Link>
            </Col>
          </Box>
        </Box>
      </CenteredPage>

      <DataPolicyModal
        show={showPrivacyPolicyModal}
        setShow={setShowPrivacyPolicyModal}
      />

      <TermsModal show={showTermsModal} setShow={setShowTermsModal} />
    </>
  );
};

SignUpView.propTypes = {
  handleSignUp: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  checkError: PropTypes.bool.isRequired,
  setCheckError: PropTypes.func.isRequired,
  setChecked: PropTypes.func.isRequired,
  checked: PropTypes.bool.isRequired,
  showPrivacyPolicyModal: PropTypes.bool.isRequired,
  setShowPrivacyPolicyModal: PropTypes.func.isRequired,
  showTermsModal: PropTypes.bool.isRequired,
  setShowTermsModal: PropTypes.func.isRequired,
  initialValues: PropTypes.object.isRequired,
  validationSchema: PropTypes.object.isRequired,
  openModal: PropTypes.func.isRequired,
};

export default SignUpView;
